import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Nav from "../components/Nav";
import api from "../lib/api";
import { markCheckoutSucceeded } from "../lib/checkoutBeacon";
// Iter XX.267b — success haptic on Plus purchase completion.
import { hapticSuccess } from "../lib/haptics";

/**
 * /plus/success — destination after Stripe Checkout completes.
 * Polls /api/billing/checkout/status/{session_id} until payment_status === 'paid'
 * (or it times out). Activation is handled idempotently on the backend.
 *
 * Iter XX.70.1 — Seamless Stripe Return.
 * When the checkout was launched from a video paywall the URL carries
 * `?return_to=video:{video_analysis_id}` (populated by
 * `plus_billing.start_checkout` when `video_analysis_id` is passed).
 * As soon as the backend confirms `payment_status === 'paid'` we skip
 * the welcome/celebration UI entirely and redirect to
 * `/chat?return_to=video:{id}` so `Chat.jsx` can auto-reveal the
 * newly-unlocked cards + append the natural continuation message.
 * Constitution §7: continuation, not celebration.
 */
export default function PlusSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  // Raw param may be URL-encoded once (`video%3A{id}`) or arrive already
  // decoded — Stripe's success_url substitution has historically been
  // inconsistent. Try both shapes.
  const rawReturnTo = params.get("return_to") || "";
  let returnTo = rawReturnTo;
  try {
    if (returnTo && !returnTo.startsWith("video:") && returnTo.includes("%3A")) {
      returnTo = decodeURIComponent(returnTo);
    }
  } catch { /* ignore */ }
  const returnsToVideo =
    typeof returnTo === "string" && returnTo.startsWith("video:") && returnTo.length > "video:".length;

  const [status, setStatus] = useState({ payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);
  const timer = useRef(null);
  const redirectedRef = useRef(false);

  useEffect(() => {
    // Iter XX.10 — clear the abandoned-checkout stash; the user made it.
    markCheckoutSucceeded();
    // Iter XX.72.6 — funnel event: user has returned from Stripe.
    try {
      api.post("/web/track", {
        session_id: null,
        event: "checkout_returned",
        payload: {
          session_id: sessionId,
          returns_to_video: returnsToVideo,
        },
      }).catch(() => {});
    } catch { /* swallow */ }
    if (!sessionId) return;
    let cancelled = false;
    let unlockedTracked = false;

    async function poll(n) {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        setStatus(data);
        // Iter XX.72.6 — fire `plus_access_unlocked` exactly once the
        // moment activation lands (the FINAL funnel event; downstream
        // dashboards close the loop here).
        if (!unlockedTracked
            && (data.plus_status === "active"
                || (data.payment_status || "").toLowerCase() === "paid")) {
          unlockedTracked = true;
          // Iter XX.267b — success notification haptic on the moment
          // Plus activation lands. Gated by `unlockedTracked` so it
          // only ever fires once per successful checkout.
          hapticSuccess();
          try {
            api.post("/web/track", {
              session_id: null,
              event: "plus_access_unlocked",
              payload: {
                stripe_session_id: sessionId,
                returns_to_video: returnsToVideo,
              },
            }).catch(() => {});
          } catch { /* swallow */ }
          // Iter XX.79 · Clarity — final funnel event. Fired ONCE when
          // Stripe confirms the payment landed, mirroring
          // `plus_access_unlocked` above so both dashboards close the
          // loop at the same moment.
          try {
            const { track } = require("../lib/clarity");
            track("gully_plus_purchased", {
              stripe_session_id: sessionId,
              returns_to_video: returnsToVideo,
            });
          } catch { /* clarity is best-effort */ }
        }
        // Iter XX.70.1 — Seamless return: as soon as payment is
        // confirmed AND the checkout carried a video return target,
        // redirect straight to the chat. No visible confirmation copy,
        // no route pause. The activation logic on the backend has
        // already run idempotently by the time payment_status flips to
        // paid.
        if ((data.payment_status || "").toLowerCase() === "paid" && returnsToVideo && !redirectedRef.current) {
          redirectedRef.current = true;
          const target = `/chat?return_to=${encodeURIComponent(returnTo)}`;
          // Use replace so the /plus/success entry doesn't linger in
          // history — pressing Back should go to the pre-Stripe page,
          // not to the confirmation page the user never really saw.
          navigate(target, { replace: true });
          return;
        }
        if (
          (data.payment_status || "").toLowerCase() === "paid" ||
          (data.status || "").toLowerCase() === "expired" ||
          n >= 8
        ) {
          return;
        }
      } catch {
        /* swallow */
      }
      setAttempts(n + 1);
      timer.current = setTimeout(() => poll(n + 1), 2000);
    }
    poll(0);
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [sessionId, returnTo, returnsToVideo, navigate]);

  const paid = (status.payment_status || "").toLowerCase() === "paid";
  const expired = (status.status || "").toLowerCase() === "expired";

  // Iter XX.70.1 — hold a minimal "returning to chat…" state while we
  // wait for the redirect to fire, so nothing celebratory ever paints.
  // A 200-400ms flash of chrome is enough to feel like an interruption.
  if (returnsToVideo && !expired) {
    return (
      <div className="grain min-h-screen" data-testid="plus-success-video-return">
        <div className="max-w-2xl mx-auto px-6 py-24">
          <p className="text-neutral-400 text-sm">Just a moment…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen">
      <Nav />
      <div className="max-w-2xl mx-auto px-6 py-20">
        {paid ? (
          <div data-testid="plus-success-paid">
            <p className="label-eyebrow mb-3" style={{ color: "var(--highlight)" }}>
              🎉 welcome to gully plus
            </p>
            <h1 className="font-display font-light text-4xl sm:text-5xl tracking-tight mb-6">
              Welcome to Gully Plus.<br />
              <span className="text-[#A7F3D0]">Let&apos;s build something special.</span>
            </h1>
            <p className="text-neutral-300 mb-6 leading-relaxed text-lg">
              Your cricket journey will now be remembered across every
              match, practice and conversation. Unlimited video reviews
              are unlocked.
            </p>
            <p className="text-neutral-400 mb-10 leading-relaxed text-sm">
              Head back to chat and pick up where you left off — Gully
              will start weaving in what you&apos;ve been working on. 🏏
            </p>
            <div className="flex gap-3 flex-wrap">
              <Link to="/chat" data-testid="plus-success-back-chat" className="btn-primary text-sm">
                Back to chat
              </Link>
              <Link
                to="/chat?upload=video"
                data-testid="plus-success-upload-video"
                className="btn-ghost text-sm"
              >
                Upload a cricket video
              </Link>
            </div>
          </div>
        ) : expired ? (
          <div data-testid="plus-success-expired">
            <h1 className="font-display text-3xl mb-4">Checkout session expired</h1>
            <p className="text-neutral-400 mb-8">
              No charge was made. You can try again whenever you like.
            </p>
            <Link to="/plus" className="btn-primary text-sm">Try again</Link>
          </div>
        ) : (
          <div data-testid="plus-success-pending">
            <p className="label-eyebrow mb-3">finalising your upgrade</p>
            <h1 className="font-display text-3xl mb-4">Confirming payment…</h1>
            <p className="text-neutral-500 text-sm">
              Hold tight — checking with Stripe. (attempt {attempts + 1}/9)
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
